import { useEffect, useState, useCallback, useRef } from 'react';
import { sdk } from '@farcaster/frame-sdk';

export const useFarcasterMiniApp = () => {
  const [isReady, setIsReady] = useState(false);
  const [isMiniApp, setIsMiniApp] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [context, setContext] = useState(null);
  const [user, setUser] = useState(null);
  
  const readyCalledRef = useRef(false);

  const callReady = useCallback(async (disableGestures = false) => {
    if (!isMiniApp || readyCalledRef.current) return;
    
    try {
      await sdk.actions.ready({
        disableNativeGestures: disableGestures
      });
      readyCalledRef.current = true;
      console.log('✅ Farcaster mini app ready signal sent');
    } catch (err) {
      console.error('❌ Error calling ready():', err);
      setError(err);
    }
  }, [isMiniApp]);

  useEffect(() => {
    const initializeMiniApp = async () => {
      try {
        setIsLoading(true);
        
        // Check if we're running inside a Farcaster client
        const frameContext = await sdk.context;
        
        if (frameContext?.client?.clientFid) {
          setIsMiniApp(true);
          setContext(frameContext);
          console.log('🏰 Running in Farcaster mini app mode');
          console.log('Client FID:', frameContext.client.clientFid);
          
          // Extract user information from the context
          const userInfo = {
            fid: frameContext.client.clientFid,
            username: frameContext.user?.username || frameContext.user?.displayName || `fid-${frameContext.client.clientFid}`,
            displayName: frameContext.user?.displayName || frameContext.user?.username || `User ${frameContext.client.clientFid}`,
            pfpUrl: frameContext.user?.pfpUrl || null,
            isAuthenticated: true,
            authenticatedAt: new Date().toISOString(),
            environment: 'farcaster-miniapp'
          };
          
          console.log('👤 Extracted user info from Farcaster context:', userInfo);
          setUser(userInfo);
          
          // Store in localStorage for persistence
          localStorage.setItem('farcaster_miniapp_user', JSON.stringify(userInfo));
          
          // Don't call ready() immediately - let the UI components decide when
          // they're ready to be shown to avoid jitter and content reflows
          setIsReady(true);
        } else {
          // Not in a Farcaster client, running as regular web app
          console.log('🌐 Running as regular web app');
          setIsReady(true);
          
          // Clear any old miniapp user data
          localStorage.removeItem('farcaster_miniapp_user');
        }
      } catch (err) {
        console.error('❌ Error initializing Farcaster mini app:', err);
        setError(err);
        // Still set ready to true so the app works even if mini app setup fails
        setIsReady(true);
        
        // Clear any old miniapp user data on error
        localStorage.removeItem('farcaster_miniapp_user');
      } finally {
        setIsLoading(false);
      }
    };

    initializeMiniApp();
  }, []);

  // Auto-call ready when the app is fully loaded (as a fallback)
  useEffect(() => {
    if (isReady && isMiniApp && !readyCalledRef.current) {
      // Add a small delay to ensure the UI has had time to render
      const timer = setTimeout(() => {
        callReady();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isReady, isMiniApp, callReady]);

  return {
    isReady,
    isMiniApp,
    isLoading,
    error,
    context,
    user,
    callReady,
    sdk: isMiniApp ? sdk : null
  };
}; 